import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { BiFilter } from 'react-icons/bi';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { SelectItem } from '~/types';

import { AutoComplete } from '../AutoComplete';
import {
  useFilterBranches,
  useFilterGroups,
  useFilterHeadOffices,
} from '~/hooks/useFilter';
import { STORAGE } from '../../constants';

export const CompanyFilter: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);

  const { groups, headOffices, branches, apply, isActive } =
    useContext(FilterContext);

  const [selectedGroups, setSelectedGroups] = useState<SelectItem[]>(groups);
  const [selectedHeadOffices, setSelectedHeadOffices] =
    useState<SelectItem[]>(headOffices);
  const [selectedBranches, setSelectedBranches] =
    useState<SelectItem[]>(branches);

  const [enableGroups, setEnableGroups] = useState(false);
  const [enableHeadOffices, setEnableHeadOffices] = useState(false);
  const [enableBranches, setEnableBranches] = useState(false);

  const {
    data: groupsOpcs,
    refetch: refetchGroups,
    isLoading: isLoadingGroups,
  } = useFilterGroups(enableGroups);

  const {
    data: headOfficesOpcs,
    refetch: refetchHeadOffices,
    isLoading: isLoadingHeadOffices,
  } = useFilterHeadOffices(selectedGroups, enableHeadOffices);

  const {
    data: branchesOpcs,
    refetch: refetchBranches,
    isLoading: isLoadingBranches,
  } = useFilterBranches(
    {
      groups: selectedGroups,
      headOffices: selectedHeadOffices,
    },
    enableBranches,
  );

  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '');

  useEffect(() => {
    const groupsLocalStorage: SelectItem[] = JSON.parse(
      localStorage.getItem('groups') || '[]',
    );
    const headOfficeLocalStorage: SelectItem[] = JSON.parse(
      localStorage.getItem('headOffices') || '[]',
    );
    const branchesLocalStorage: SelectItem[] = JSON.parse(
      localStorage.getItem('branches') || '[]',
    );
    if (!isActive && groupsLocalStorage?.length) {
      apply({
        newgroups: groupsLocalStorage,
        newheadOffices: headOfficeLocalStorage,
        newbranches: branchesLocalStorage,
      });
    }
  }, []);

  const handleSelectedGroupsChange = (selected?: SelectItem[]) => {
    if (selected) {
      setSelectedGroups(selected);
    }
    setSelectedHeadOffices([]);
    setSelectedBranches([]);
  };

  const handleSelectedHeadOfficesChange = (selected?: SelectItem[]) => {
    if (selected) {
      setSelectedHeadOffices(selected);
    }
    setSelectedBranches([]);
  };

  const handleSelectedBranchesChange = (selected?: SelectItem[]) => {
    if (selected) setSelectedBranches(selected);
  };

  const handleSubmit = () => {
    apply({
      newgroups: selectedGroups,
      newbranches: selectedBranches,
      newheadOffices: selectedHeadOffices,
    });
    localStorage.setItem('groups', JSON.stringify(selectedGroups));
    localStorage.setItem('headOffices', JSON.stringify(selectedHeadOffices));
    localStorage.setItem('branches', JSON.stringify(selectedBranches));
    onClose();
  };

  const handleOpen = () => {
    setSelectedBranches(branches);
    setSelectedGroups(groups);
    setSelectedHeadOffices(headOffices);
    onOpen();
  };

  const handleClose = () => {
    setSelectedBranches(branches);
    setSelectedGroups(groups);
    setSelectedHeadOffices(headOffices);
    onClose();
  };

  const handleClear = () => {
    setSelectedBranches([]);
    setSelectedGroups([]);
    setSelectedHeadOffices([]);
  };

  const emptylistGroups = [
    {
      value: '0',
      label: isLoadingGroups ? 'Carregando...' : 'Nenhum item encontrado',
    },
  ];
  const emptylistHeadOffices = [
    {
      value: '0',
      label: isLoadingHeadOffices ? 'Carregando...' : 'Nenhum item encontrado',
    },
  ];
  const emptylistBranches = [
    {
      value: '0',
      label: isLoadingBranches ? 'Carregando...' : 'Nenhum item encontrado',
    },
  ];

  const isDisable = (): boolean => {
    switch (user.accessType) {
      case 'HEAD_OFFICE': {
        return (
          (!selectedGroups?.length && selectedHeadOffices?.length > 0) ||
          (selectedGroups?.length > 0 && !selectedHeadOffices?.length)
        );
        break;
      }
      case 'BRANCH': {
        return (
          (!selectedGroups?.length && selectedBranches?.length > 0) ||
          (selectedGroups?.length > 0 && !selectedBranches?.length) ||
          (selectedGroups?.length > 0 &&
            selectedHeadOffices?.length > 0 &&
            !selectedBranches?.length)
        );
        break;
      }
      default: {
        return false;
        break;
      }
    }
  };

  useEffect(() => {
    enableGroups && setEnableGroups(false);
    enableHeadOffices && setEnableHeadOffices(false);
    enableBranches && setEnableBranches(false);
  }, [enableGroups, enableHeadOffices, enableBranches]);

  const onClickGroups = () => {
    setEnableGroups(true);
  };

  const onClickHeadOffices = () => {
    setEnableHeadOffices(true);
  };

  const onClickBranches = () => {
    setEnableBranches(true);
  };

  return (
    <>
      <Box
        as="button"
        ref={btnRef}
        onClick={handleOpen}
        height="24px"
        lineHeight="1.2"
        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
        border="1px"
        px="8px"
        borderRadius="4px"
        fontSize="sm"
        bg="transparent"
        borderColor={isActive ? 'green.400' : '#fff'}
        color={isActive ? 'green.400' : '#fff'}
      >
        <Flex align="center" gridGap="2">
          <Icon as={BiFilter} />
          <Text fontSize={{ lg: 'sm', md: 'sm' }}>Filtro</Text>
        </Flex>
      </Box>
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>Filtro</DrawerHeader>
          <DrawerBody>
            <Flex justifyContent="end">
              <Button onClick={handleClear} variant="link" size="xs">
                Limpar todos
              </Button>
            </Flex>
            <AutoComplete
              items={groupsOpcs?.length ? groupsOpcs : emptylistGroups}
              placeholder="Grupos"
              label="Selecione um ou mais Grupos"
              listStyleProps={{
                maxHeight: '200px',
                overflowY: 'scroll',
                padding: '0',
                marginTop: '-11px',
                position: 'absolute',
                zIndex: '3',
                width: '100%',
              }}
              selectedItems={selectedGroups}
              onSelectedItemsChange={changes =>
                handleSelectedGroupsChange(changes.selectedItems)
              }
              disableItems={!groupsOpcs?.length}
              onClick={onClickGroups}
            />
            <AutoComplete
              label="Selecione uma ou mais Matrizes"
              placeholder="Matrizes"
              items={
                headOfficesOpcs?.length ? headOfficesOpcs : emptylistHeadOffices
              }
              listStyleProps={{
                maxHeight: '200px',
                overflowY: 'scroll',
                padding: '0',
                marginTop: '-11px',
                position: 'absolute',
                zIndex: '3',
                width: '100%',
              }}
              selectedItems={selectedHeadOffices}
              onSelectedItemsChange={changes => {
                handleSelectedHeadOfficesChange(changes.selectedItems);
              }}
              disableItems={!headOfficesOpcs?.length}
              onClick={onClickHeadOffices}
            />
            <AutoComplete
              label="Selecione uma ou mais Filiais"
              placeholder="Filiais"
              items={branchesOpcs?.length ? branchesOpcs : emptylistBranches}
              listStyleProps={{
                maxHeight: '200px',
                overflowY: 'scroll',
                padding: '0',
                marginTop: '-11px',
                position: 'absolute',
                zIndex: '3',
                width: '100%',
              }}
              selectedItems={selectedBranches}
              onSelectedItemsChange={changes => {
                handleSelectedBranchesChange(changes.selectedItems);
              }}
              disableItems={!branchesOpcs?.length}
              onClick={onClickBranches}
            />
            <Button
              type="submit"
              onClick={handleSubmit}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              disabled={isDisable()}
            >
              Aplicar
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
